import { Trans, useTranslation } from 'react-i18next';

import {
    FilledCard,
    Flex,
    Heading2,
    HorizontalDivider,
    IconCheckCircle,
    Stack,
} from '@myphx-engineering/phx-design-system';

import {
    useFillPathway,
    useFillPrescription,
} from '../../../contexts/fill/use-fill-prescription';
import { useProgramTerms } from '../../../hooks/use-program-terms';

import { ProgramSavingsCard } from './ProgramSavingsCard';
import {
    SavingsCardDetailsLabel,
    SavingsCardDetailsValue,
} from './SavingsCardDetails';

export const SavingsCardTeaser = () => {
    const { t } = useTranslation('lilly');
    const { allOffers, coverage, medication } = useFillPrescription();
    const { pathway } = useFillPathway();

    const terms = useProgramTerms(medication, pathway);

    let price: number | null | undefined;

    switch (pathway) {
        case 'insurance':
            price = allOffers.find(
                (o) => o.headlinePriceType === 'CopayAssistance'
            )?.patientCost;
            break;
        case 'self-pay': {
            const best =
                allOffers.find((o) => o.headlinePriceType === 'Incentive') ??
                allOffers.find((o) => o.headlinePriceType === 'CashAssistance');
            price = best?.patientCost;
            break;
        }
    }

    if (!terms?.savingsCard) {
        return null;
    }

    const description = price ? (
        <Trans
            t={t}
            i18nKey="savingsCard.teaser.description.withPrice"
            values={{
                cardName: terms.savingsCard.name,
                price,
            }}
        />
    ) : (
        <Trans
            t={t}
            i18nKey="savingsCard.teaser.description.noPrice"
            values={{ cardName: terms.savingsCard.name }}
        />
    );

    return (
        <FilledCard
            withBorder
            color="primary"
            header={
                <Stack gap="sm">
                    <Flex justify="space-between">
                        <Heading2>
                            {coverage === 'COVERED' && pathway === 'insurance'
                                ? t('savingsCard.notice.headerCovered')
                                : t('savingsCard.notice.header', {
                                      cardType: terms.savingsCard.type,
                                  })}
                        </Heading2>
                        <IconCheckCircle color="primary" />
                    </Flex>
                    <HorizontalDivider />
                </Stack>
            }
        >
            <ProgramSavingsCard
                data-testid="attestation-savings-card-teaser"
                patientInfo={
                    <SavingsCardDetailsLabel>
                        {t('savingsCard.teaser.yourInfo')}
                    </SavingsCardDetailsLabel>
                }
                savingsCard={terms.savingsCard}
            >
                <SavingsCardDetailsValue>{description}</SavingsCardDetailsValue>
            </ProgramSavingsCard>
        </FilledCard>
    );
};
